import { useContext } from 'react'

import { format } from 'date-fns'
import { FormattedMessage } from 'react-intl'
import { shallowEqual, useSelector } from 'react-redux'
import Flex from 'styled-flex-component'

import AnalyticsPostCampaignReportEmpty from '@components/Analytics/PostCampaignReport/ListView/Empty/Empty'
import AnalyticsPostCampaignReportListViewAmount from '@components/Analytics/PostCampaignReport/ListView/Item/Amount'
import {
  Entry,
  RowWrapper,
} from '@components/Analytics/PostCampaignReport/ListView/ListView.styled'
import LockableEntry from '@components/Analytics/PostCampaignReport/ListView/LockableEntry/LockableEntry'
import { isNumber } from '@components/Analytics/PrePurchase/PrePurchase'
import { AnalyticsCustomPrice } from '@components/UI/Price/Price'
import { FULL_DATE } from '@constants'
import { CampaignContext } from '@context/Campaign'
import { PCRChannels } from '@enums'
import { IApplicationState } from '@store'
import { selectCampaignTotalCurrency } from '@store/campaigns'
import { isSupportedPCRMetricsChannel, selectSubmissionMetrics } from '@store/postCampaignReport'
import { selectPcrMetrics } from '@store/postCampaignReport/metrics/selectors'
import { IPCRMetrics } from '@store/postCampaignReport/metrics/types'
import Theme from '@theme'
import { Container, Text } from '@tribegroup'
import { abbreviateNumber } from '@utils'

interface IProps {
  submissionId: number
  backgroundColor: string
  activeChannel: PCRChannels
}

export const AnalyticsPostCampaignReportSubmissionListItem: React.FC<IProps> = ({
  submissionId,
  backgroundColor,
  activeChannel,
}) => {
  const {
    campaign: { is_price_hidden: isPriceHidden, id: campaignId },
  } = useContext(CampaignContext)

  const { currency, metrics } = useSelector((state: IApplicationState) => {
    let metrics = selectSubmissionMetrics(state, submissionId) as IPCRMetrics
    if (isSupportedPCRMetricsChannel(activeChannel)) {
      metrics = selectPcrMetrics(state, activeChannel, submissionId)
    }
    return {
      metrics,
      currency: selectCampaignTotalCurrency(state, campaignId),
    }
  }, shallowEqual)

  if (!metrics || !metrics?.published_at || !metrics.likes) {
    return (
      <Container>
        <AnalyticsPostCampaignReportEmpty
          minWidth={isPriceHidden ? 58.5 : 84.375}
          backgroundColor={backgroundColor}
        />
      </Container>
    )
  }

  return (
    <Container inline relative height={4.75}>
      <Flex>
        <RowWrapper
          data-testid={`${activeChannel}-published-date`}
          backgroundColor={backgroundColor}
        >
          <Entry>
            <Text color={Theme.grey900Color}>
              {format(new Date(metrics.published_at), FULL_DATE)}
            </Text>
          </Entry>
        </RowWrapper>
        {!isPriceHidden && (
          <AnalyticsPostCampaignReportListViewAmount
            backgroundColor={backgroundColor}
            testId="${testIdPrefix}-purchase-amount"
            pattern="!###,### "
            value={{ amount: metrics?.purchase_amount, currency }}
          />
        )}
        <RowWrapper data-testid={`${activeChannel}-followers`} backgroundColor={backgroundColor}>
          <Entry>
            <Text color={Theme.grey900Color}>{abbreviateNumber(metrics.followers)}</Text>
          </Entry>
        </RowWrapper>
        <RowWrapper data-testid={`${activeChannel}-likes`} backgroundColor={backgroundColor}>
          <LockableEntry
            empty={!metrics?.likes}
            content={
              isNumber(metrics?.likes) && (
                <Text color={Theme.grey900Color}>{abbreviateNumber(metrics?.likes ?? 0)}</Text>
              )
            }
          />
        </RowWrapper>
        <RowWrapper data-testid={`${activeChannel}-comments`} backgroundColor={backgroundColor}>
          <LockableEntry
            empty={!metrics?.comments}
            content={
              isNumber(metrics?.comments) && (
                <Text color={Theme.grey900Color}>{abbreviateNumber(metrics?.comments ?? 0)}</Text>
              )
            }
          />
        </RowWrapper>
        <RowWrapper data-testid={`${activeChannel}-shares`} backgroundColor={backgroundColor}>
          <LockableEntry
            empty={!metrics?.shares}
            content={
              isNumber(metrics?.shares) && (
                <Text color={Theme.grey900Color}>{abbreviateNumber(metrics?.shares ?? 0)}</Text>
              )
            }
          />
        </RowWrapper>
        <RowWrapper data-testid={`${activeChannel}-engagement`} backgroundColor={backgroundColor}>
          <LockableEntry
            empty={!metrics?.engagement}
            content={
              isNumber(metrics?.engagement) && (
                <Text color={Theme.grey900Color}>{abbreviateNumber(metrics?.engagement ?? 0)}</Text>
              )
            }
          />
        </RowWrapper>
        <RowWrapper
          data-testid={`${activeChannel}-engagement-pct`}
          backgroundColor={backgroundColor}
        >
          <LockableEntry
            empty={!metrics?.engagement_pct}
            content={
              isNumber(metrics?.engagement_pct) && (
                <Text color={Theme.grey900Color}>
                  <FormattedMessage
                    id="core.text.percent"
                    values={{ value: metrics?.engagement_pct }}
                  />
                </Text>
              )
            }
          />
        </RowWrapper>
        {!isPriceHidden && (
          <RowWrapper data-testid={`${activeChannel}-cpe`} backgroundColor={backgroundColor}>
            <LockableEntry
              empty={!metrics?.cpe}
              content={
                isNumber(metrics?.cpe) && (
                  <Text color={Theme.grey900Color}>
                    <AnalyticsCustomPrice currency={currency} quantity={metrics.cpe!} />
                  </Text>
                )
              }
            />
          </RowWrapper>
        )}

        <RowWrapper data-testid={`${activeChannel}-impressions`} backgroundColor={backgroundColor}>
          <LockableEntry
            empty={!metrics?.impressions}
            content={
              isNumber(metrics?.impressions) && (
                <Text color={Theme.grey900Color}>
                  {abbreviateNumber(metrics?.impressions ?? 0)}
                </Text>
              )
            }
          />
        </RowWrapper>
        <RowWrapper
          data-testid={`${activeChannel}-impressions-pct`}
          backgroundColor={backgroundColor}
        >
          <LockableEntry
            empty={!metrics?.impressions_pct}
            content={
              isNumber(metrics?.impressions_pct) && (
                <Text color={Theme.grey900Color}>
                  <FormattedMessage
                    id="core.text.percent"
                    values={{ value: metrics?.impressions_pct }}
                  />
                </Text>
              )
            }
          />
        </RowWrapper>
        {!isPriceHidden && (
          <>
            <RowWrapper data-testid={`${activeChannel}-cpm`} backgroundColor={backgroundColor}>
              <LockableEntry
                empty={!metrics?.cpm}
                content={
                  isNumber(metrics?.cpm) && (
                    <Text color={Theme.grey900Color}>
                      <AnalyticsCustomPrice currency={currency} quantity={metrics.cpm!} />
                    </Text>
                  )
                }
              />
            </RowWrapper>
            <RowWrapper data-testid={`${activeChannel}-cpv`} backgroundColor={backgroundColor}>
              <LockableEntry
                empty={!metrics?.cpv}
                content={
                  isNumber(metrics?.cpv) && (
                    <Text color={Theme.grey900Color}>
                      <AnalyticsCustomPrice currency={currency} quantity={metrics.cpv!} />
                    </Text>
                  )
                }
              />
            </RowWrapper>
          </>
        )}
      </Flex>
    </Container>
  )
}
